import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Phone, MessageCircle } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_property-inquiry-hub/artifacts/10zghdqz_IMG_PVH.png';

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/projects', label: 'Projects' },
    { path: '/builders', label: 'Builders' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  const phoneNumber = '+917020970161';
  const whatsappNumber = '917020970161';

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-dark shadow-2xl' : 'bg-dark/95 backdrop-blur-md'
      }`}
    >
      {/* Top Bar with Contact */}
      <div className="hidden lg:block border-b border-dark-lighter">
        <div className="px-6 md:px-12 lg:px-24 py-3 flex items-center justify-between">
          <p className="text-white/60 text-sm italic font-heading">"Trusted Property. Real Advantage."</p>
          <div className="flex items-center gap-6">
            <a 
              href={`tel:${phoneNumber}`} 
              className="flex items-center gap-2 text-white/80 hover:text-rose-gold transition-colors text-sm"
            >
              <Phone size={16} />
              <span className="font-semibold">+91 70209 70161</span>
            </a>
            <a 
              href={`https://wa.me/${whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/80 hover:text-green-500 transition-colors text-sm"
            >
              <MessageCircle size={16} />
              <span className="font-semibold">WhatsApp</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-4 group">
          <img 
            src={LOGO_URL} 
            alt="PropVanta" 
            className="h-12 w-auto transition-transform duration-300 group-hover:scale-105" 
          />
          <div>
            <span className="font-heading text-3xl font-bold text-rose-gold tracking-wider block">PropVanta</span>
            <span className="text-white/40 text-xs uppercase tracking-widest">Property Consultants</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={`font-medium text-sm uppercase tracking-widest transition-all duration-300 relative ${
                location.pathname === link.path
                  ? 'text-rose-gold'
                  : 'text-white/80 hover:text-rose-gold'
              }`}
            >
              {link.label}
              {location.pathname === link.path && (
                <motion.div
                  layoutId="underline"
                  className="absolute -bottom-1 left-0 right-0 h-0.5 bg-rose-gold"
                />
              )}
            </Link>
          ))}
          <a
            href={`tel:${phoneNumber}`}
            data-testid="header-cta-button"
            className="btn-primary flex items-center gap-2 text-sm uppercase tracking-wider"
          >
            <Phone size={16} />
            Call Now
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-rose-gold"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-dark-light border-t border-dark-lighter"
        >
          <nav className="flex flex-col px-6 py-6 gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className={`font-medium text-sm uppercase tracking-widest py-2 ${
                  location.pathname === link.path
                    ? 'text-rose-gold'
                    : 'text-white/80 hover:text-rose-gold'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex flex-col gap-3 pt-4 border-t border-dark-lighter">
              <a
                href={`tel:${phoneNumber}`}
                className="btn-primary text-center flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
              >
                <Phone size={16} />
                Call Now
              </a>
              <a
                href={`https://wa.me/${whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline text-center flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
              >
                <MessageCircle size={16} />
                WhatsApp
              </a>
            </div>
          </nav>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Header;
